import { useState, useMemo } from 'react';
import { Button, Badge } from './ui';
import { useSubmitFeedback } from '../hooks/useApi';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
  ThumbsUp,
  ThumbsDown,
  Send,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

interface PIIAuditPanelProps {
  /** Original (unredacted) resume text */
  originalText: string | null;
  /** PII-redacted version of resume text */
  sanitizedText: string | null;
  /** Application ID for feedback submission */
  applicationId: number;
  /** Candidate name for display */
  candidateName?: string;
  /** When was the resume last processed */
  processedAt?: string | null;
}

/** PII placeholder patterns that appear in redacted text.
 *  Must match the actual placeholders produced by pii-sanitizer.ts and pii-vault.ts:
 *  [FirstName], [LastName], [FullName], [Email], [Phone], [Location], [SSN], [Address], [Address_N], [LinkedIn]
 */
const PII_PATTERNS = [
  { pattern: /\[FullName\]/g, label: 'Full Name', color: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' },
  { pattern: /\[FirstName\]/g, label: 'First Name', color: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' },
  { pattern: /\[LastName\]/g, label: 'Last Name', color: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' },
  { pattern: /\[Email\]/g, label: 'Email', color: 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200' },
  { pattern: /\[Phone\]/g, label: 'Phone', color: 'bg-pink-200 dark:bg-pink-800 text-pink-800 dark:text-pink-200' },
  { pattern: /\[Location\]/g, label: 'Location', color: 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200' },
  { pattern: /\[SSN\]/g, label: 'SSN', color: 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200' },
  { pattern: /\[Address(?:_\d+)?\]/g, label: 'Address', color: 'bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200' },
  { pattern: /\[LinkedIn\]/g, label: 'LinkedIn', color: 'bg-sky-200 dark:bg-sky-800 text-sky-800 dark:text-sky-200' },
];

/**
 * Count PII replacements found in the sanitized text
 */
function countReplacements(text: string): { total: number; byType: Record<string, number> } {
  const byType: Record<string, number> = {};
  let total = 0;

  for (const { pattern, label } of PII_PATTERNS) {
    const matches = text.match(pattern);
    if (matches) {
      byType[label] = matches.length;
      total += matches.length;
    }
  }

  return { total, byType };
}

/**
 * Highlight PII placeholders in the sanitized text with colored badges
 */
function HighlightedText({ text }: { text: string }) {
  const segments = useMemo(() => {
    // Build a combined regex from all patterns
    const allPatterns = PII_PATTERNS.map(p => p.pattern.source).join('|');
    const combinedRegex = new RegExp(`(${allPatterns})`, 'g');

    const parts: Array<{ text: string; isPII: boolean; color?: string; label?: string }> = [];
    let lastIndex = 0;
    let match;

    // Reset lastIndex on combined regex
    combinedRegex.lastIndex = 0;

    while ((match = combinedRegex.exec(text)) !== null) {
      // Add non-PII text before this match
      if (match.index > lastIndex) {
        parts.push({ text: text.slice(lastIndex, match.index), isPII: false });
      }

      // Find which pattern matched
      const matchedText = match[0];
      const patternInfo = PII_PATTERNS.find(p => new RegExp(p.pattern.source).test(matchedText));

      parts.push({
        text: matchedText,
        isPII: true,
        color: patternInfo?.color || 'bg-gray-200 dark:bg-gray-700',
        label: patternInfo?.label || 'PII',
      });

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push({ text: text.slice(lastIndex), isPII: false });
    }

    return parts;
  }, [text]);

  return (
    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
      {segments.map((seg, i) =>
        seg.isPII ? (
          <span
            key={i}
            className={`inline-block px-1 py-0.5 rounded text-xs font-semibold ${seg.color}`}
            title={seg.label}
          >
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </div>
  );
}

export function PIIAuditPanel({
  originalText,
  sanitizedText,
  applicationId,
  candidateName,
  processedAt,
}: PIIAuditPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [feedbackState, setFeedbackState] = useState<'none' | 'good' | 'bad'>('none');
  const [feedbackText, setFeedbackText] = useState('');

  const submitMutation = useSubmitFeedback();

  const replacementStats = useMemo(() => {
    if (!sanitizedText) return null;
    return countReplacements(sanitizedText);
  }, [sanitizedText]);

  const handleFeedback = async (quality: 'good' | 'bad') => {
    if (quality === 'good') {
      // Submit positive feedback immediately
      try {
        await submitMutation.mutateAsync({
          applicationId,
          feedbackType: 'pii_quality',
          content: `PII redaction quality: GOOD${candidateName ? ` (for ${candidateName})` : ''}. No issues found with the redaction.`,
        });
        setFeedbackState('good');
      } catch (err) {
        console.error('Failed to submit PII feedback:', err);
      }
    } else {
      // Show feedback form for negative feedback
      setFeedbackState('bad');
    }
  };

  const handleSubmitNegativeFeedback = async () => {
    if (!feedbackText.trim()) return;

    try {
      await submitMutation.mutateAsync({
        applicationId,
        feedbackType: 'pii_quality',
        content: `PII redaction quality: NEEDS IMPROVEMENT${candidateName ? ` (for ${candidateName})` : ''}. Issues: ${feedbackText.trim()}`,
      });
      setFeedbackText('');
    } catch (err) {
      console.error('Failed to submit PII feedback:', err);
    }
  };

  // Don't render if there's no sanitized text
  if (!sanitizedText) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900">
            <Shield className="h-4 w-4 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              PII Redaction Audit
            </h3>
            {replacementStats && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {replacementStats.total} PII item{replacementStats.total !== 1 ? 's' : ''} redacted
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {feedbackState === 'good' && (
            <Badge variant="success" size="xs">Approved</Badge>
          )}
          {feedbackState === 'bad' && (
            <Badge variant="warning" size="xs">Flagged</Badge>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Stats bar */}
          {replacementStats && Object.keys(replacementStats.byType).length > 0 && (
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-wrap gap-2">
                {Object.entries(replacementStats.byType).map(([label, count]) => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    {label}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
              {processedAt && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  Last processed: {new Date(processedAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Toggle original/redacted view */}
          <div className="px-4 py-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Viewing: {showOriginal ? 'Original Text' : 'PII-Redacted Text'}
              </span>
            </div>
            {originalText && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOriginal(!showOriginal)}
                leftIcon={showOriginal ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
              >
                {showOriginal ? 'Show Redacted' : 'Show Original'}
              </Button>
            )}
          </div>

          {/* Text content */}
          <div className="p-4 max-h-96 overflow-y-auto scrollbar-thin">
            {showOriginal && originalText ? (
              <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono leading-relaxed">
                {originalText}
              </pre>
            ) : (
              <HighlightedText text={sanitizedText} />
            )}
          </div>

          {/* Feedback section */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            {feedbackState === 'none' && !submitMutation.isSuccess && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Is this PII redaction accurate?
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFeedback('good')}
                    loading={submitMutation.isPending}
                    className="text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                  >
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    Good
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFeedback('bad')}
                    className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
                  >
                    <ThumbsDown className="h-4 w-4 mr-1" />
                    Needs Work
                  </Button>
                </div>
              </div>
            )}

            {feedbackState === 'good' && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <ShieldCheck className="h-4 w-4" />
                Redaction quality approved. Thank you!
              </div>
            )}

            {feedbackState === 'bad' && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <ShieldAlert className="h-4 w-4" />
                  What PII was missed or incorrectly redacted?
                </div>
                <textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder='e.g., "The nickname Ed was not redacted on line 3", "LinkedIn URL still visible", "Company name reveals identity"...'
                  rows={3}
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-violet-500 focus:ring-1 focus:ring-violet-500 resize-y"
                />

                {submitMutation.isError && (
                  <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4" />
                    Failed to submit: {(submitMutation.error as Error).message}
                  </div>
                )}

                {submitMutation.isSuccess && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <CheckCircle className="h-4 w-4" />
                    Feedback submitted. This will be used to improve PII redaction.
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      setFeedbackState('none');
                      setFeedbackText('');
                    }}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmitNegativeFeedback}
                    disabled={!feedbackText.trim()}
                    loading={submitMutation.isPending}
                    leftIcon={<Send className="h-3 w-3" />}
                  >
                    Submit Feedback
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

PIIAuditPanel.displayName = 'PIIAuditPanel';
